<script setup>
  import { computed, inject } from 'vue'
  import { PLAN_YEAR_EVENTS } from '@/shared/utils/planYearEvents'
  import BcsDynamicText from '../BcsDynamicText.vue'
  import { get } from 'lodash'

  // Define emits
  defineEmits(['close'])

  const props = defineProps({
    // New plan start date
    planStartDate: {
      type: String,
      required: true
    },
    employerName: {
      type: String,
      required: true
    },
    // New plan year ID
    planYearId: {
      type: Number,
      required: true
    },
    currentPlanStartDate: {
      type: String,
      required: false,
      default: ''
    },
    selectionMode: {
      type: Boolean,
      default: false
    },
    actualEmployer: {
      type: Object,
      default: () => null
    },
    isFromDuplicateButton: {
      type: Boolean,
      default: false
    }
  })

  // Get custom navigation from inject
  const customNavigation = inject('customNavigation')
  // Get emitter
  const emitter = inject('emitter')

  // Format date for display (MM/DD/YYYY to MM/DD/YYYY)
  const formattedPlanStartDate = computed(() => {
    if (!props.planStartDate) return ''
    
    // If already in MM/DD/YYYY format, return as is
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(props.planStartDate)) {
      return props.planStartDate
    }
    
    // Otherwise try to parse and format
    try {
      const date = new Date(props.planStartDate)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const year = date.getFullYear()
      return `${month}/${day}/${year}`
    } catch (e) {
      return props.planStartDate
    }
  })

  // Navigate to employer dashboard
  const goToEmployerDashboard = (planYearId) => {
    if (customNavigation && customNavigation.value) {
      customNavigation.value.navigate({
        newPage: customNavigation.value.routes.DetailEmployer.name,
        subPage: customNavigation.value.routes.DetailEmployer.children[0].name,
        params: { planYearId }
      })
    }
  }
</script>

<template>
  <div class="text-center px-4 pt-4">
    <div class="position-absolute top-0 end-0 p-3">
      <button type="button" class="btn-close" @click="$emit('close')" aria-label="Close"></button>
    </div>
    <div class="mb-3 mt-2">
      <p class="card-text text-14 text-gray-700">
        <i class="bi bi-check-circle"></i>
      </p>
    </div>
    <div class="mb-3">
      <span class="header-22"><strong>{{ employerName }} {{ formattedPlanStartDate }} Plan Year </strong><br></span>
      <span class="header-22"><strong>Renewal Confirmed!</strong></span>
    </div>
    <p class="mb-4 text-14 px-4">
      <BcsDynamicText text="renewalSuccessText" key="renewalSuccessText" :variables="{ employerName, formattedPlanStartDate }"/>
    </p>
    <div class="d-grid gap-3 mb-3 px-4">
      <button 
        type="button" 
        id="goToEmployerDashboardRenewalSuccess"
        class="btn btn-primary py-2"
        @click="() => {
          if (selectionMode || (props.isFromDuplicateButton && !get(props.actualEmployer, 'id'))) {
            goToEmployerDashboard(props.planYearId)
          } else {            
            emitter.emit(PLAN_YEAR_EVENTS.CHANGE_PLAN_YEAR, { planYearId: props.planYearId, goBackToDashboard: true })
          }
        }"
      >
        Go to {{ employerName }} {{ formattedPlanStartDate }} 
        <span class="d-inline-flex align-items-center text-nowrap">
          Dashboard <i class="bi bi-chevron-right ms-1"></i>
        </span>
      </button>
      <button 
        type="button" 
        id="backToAllEmployersRenewalSuccess"
        class="btn btn-link text-decoration-none pb-1"
        @click="$emit('close')">
        {{ !selectionMode ? `Back ${props.currentPlanStartDate ? 'to ' +  props.currentPlanStartDate + ' Plan' : ''}` : 'Back to All Employers Dashboard' }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.circle-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
}

.bi-check-circle {
  font-size: 86px;
  color: #198754;
}
</style>
